/**
 * Cola local de fichajes sin conexión (IndexedDB). Cada evento conserva la hora del dispositivo y un identificador
 * único; al recuperar la conexión se sincroniza y el servidor lo marca como hora DEVICE (nunca inventa hora de servidor).
 */
const DB_NAME = 'wulftime-offline';
const STORE = 'queue';

export interface QueuedEvent {
  clientEventId: string;
  scope: string; // 'worker:<employeeId>' | 'kiosk:<kioskId>'
  action: 'CLOCK_IN' | 'BREAK_START' | 'BREAK_END' | 'CLOCK_OUT';
  deviceRecordedAt: string;
  gps?: unknown;
  reason?: string | null;
  identity?: unknown;
  attempts: number;
  lastError?: string;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'clientEventId' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const r = fn(t.objectStore(STORE));
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

export const newClientEventId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export async function enqueue(e: Omit<QueuedEvent, 'attempts'>) {
  await tx('readwrite', (s) => s.put({ ...e, attempts: 0 }));
}

export async function pending(scope: string): Promise<QueuedEvent[]> {
  const all = (await tx('readonly', (s) => s.getAll())) as QueuedEvent[];
  return all.filter((e) => e.scope === scope).sort((a, b) => a.deviceRecordedAt.localeCompare(b.deviceRecordedAt));
}

export async function remove(id: string) {
  await tx('readwrite', (s) => s.delete(id));
}

export async function markFailed(e: QueuedEvent, error: string) {
  await tx('readwrite', (s) => s.put({ ...e, attempts: e.attempts + 1, lastError: error }));
}
